import { connectorList } from './connectorsConfig';
import { audioConnectors, powerConnectors, dmxConnectors } from './connectorsConfig';
import { loadPricingConfig } from './pricingConfigServiceRDS';
import { getAdminToken } from './authService';

// Real-time pricing update subscription
let pricingEventSource = null;
let pricingUpdateCallbacks = [];

// Initialize real-time pricing updates
export const initializePricingUpdates = (clientId) => {
  if (pricingEventSource) {
    pricingEventSource.close();
  }

  pricingEventSource = new EventSource(`/api/config-manager?action=subscribe&clientId=${clientId}`);
  
  pricingEventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      handlePricingUpdate(data);
    } catch (error) {
      console.error('Error parsing pricing update:', error);
    }
  };

  pricingEventSource.onerror = (error) => {
    console.error('Pricing updates connection error:', error);
    // Attempt to reconnect after 5 seconds
    setTimeout(() => {
      if (pricingEventSource) {
        initializePricingUpdates(clientId);
      }
    }, 5000);
  };

  return pricingEventSource;
};

// Subscribe to pricing updates
export const subscribeToPricingUpdates = (callback) => {
  pricingUpdateCallbacks.push(callback);
  return () => {
    const index = pricingUpdateCallbacks.indexOf(callback);
    if (index > -1) {
      pricingUpdateCallbacks.splice(index, 1);
    }
  };
};

// Handle pricing updates
const handlePricingUpdate = (update) => {
  // console.log('Received pricing update:', update);
  
  // Notify all subscribers
  pricingUpdateCallbacks.forEach(callback => {
    try {
      callback(update);
    } catch (error) {
      console.error('Error in pricing update callback:', error);
    }
  });

  // Update local cache if needed
  if (update.type === 'pricing_updated' || update.type === 'bulk_pricing_updated') {
    // Clear any cached pricing data to force reload
    clearPricingCache();
    // Also refresh labor costs cache
    refreshLaborCostsCache();
  }
};

// Clear pricing cache
const clearPricingCache = () => {
  // Clear any cached pricing data
  if (window.pricingCache) {
    delete window.pricingCache;
  }
  // Clear labor costs cache as well
  refreshLaborCostsCache();
};

// Get pricing history
export const getPricingHistory = async (limit = 50, offset = 0) => {
  try {
    const response = await fetch(`/api/config-manager?action=history&limit=${limit}&offset=${offset}`);
    if (!response.ok) {
      throw new Error('Failed to fetch pricing history');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching pricing history:', error);
    return { history: [], total: 0, limit, offset };
  }
};

// Get recent pricing changes
export const getRecentPricingChanges = async (hours = 24) => {
  try {
    const response = await fetch(`/api/config-manager?action=changes&hours=${hours}`);
    if (!response.ok) {
      throw new Error('Failed to fetch recent changes');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching recent changes:', error);
    return { changes: [], period: `${hours} hours`, count: 0 };
  }
};

// Update pricing (admin only)
export const updatePricing = async (section, data, reason) => {
  const adminToken = getAdminToken();
  if (!adminToken) {
    throw new Error('Admin access required');
  }

  try {
    const response = await fetch('/api/config-manager?action=update', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'adminToken': adminToken
      },
      body: JSON.stringify({ section, data, reason })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update pricing');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating pricing:', error);
    throw error;
  }
};

// Bulk update pricing (admin only)
export const bulkUpdatePricing = async (updates, reason) => {
  const adminToken = getAdminToken();
  if (!adminToken) {
    throw new Error('Admin access required');
  }

  try {
    const response = await fetch('/api/config-manager?action=bulk-update', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'adminToken': adminToken
      },
      body: JSON.stringify({ updates, reason })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to bulk update pricing');
    }

    return await response.json();
  } catch (error) {
    console.error('Error bulk updating pricing:', error);
    throw error;
  }
};

// Send notification to all clients (admin only)
export const sendPricingNotification = async (message, type = 'notification') => {
  const adminToken = getAdminToken();
  if (!adminToken) {
    throw new Error('Admin access required');
  }

  try {
    const response = await fetch('/api/config-manager?action=notify', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'adminToken': adminToken
      },
      body: JSON.stringify({ message, type })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send notification');
    }

    return await response.json();
  } catch (error) {
    console.error('Error sending notification:', error);
    throw error;
  }
};

// Cache for connector pricing from database
let connectorPricingCache = null;
let connectorPricingCacheTime = 0;
const CONNECTOR_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache for labor costs from database
let laborCostsCache = null;
let laborCostsCacheTime = 0;
const LABOR_COSTS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Load connector pricing from S3 file (fast & reliable)
async function loadConnectorPricingFromS3() {
  try {
    const config = await loadPricingConfig(false); // Client mode - use S3
    
    if (config && (config.source === 'S3' || config.source === 'RDS')) {
      connectorPricingCache = {
        panelMountPricing: config.panelMountPricing || {},
        inlinePricing: config.inlinePricing || {}
      };
      connectorPricingCacheTime = Date.now();
      console.log('💰 Loaded connector pricing from S3:', {
        panelMountCount: Object.keys(connectorPricingCache.panelMountPricing).length,
        inlineCount: Object.keys(connectorPricingCache.inlinePricing).length
      });
    } else {
      throw new Error('Received invalid pricing data source');
    }
  } catch (error) {
    console.error('❌ Cannot load connector pricing:', error.message);
    throw error; // Re-throw the error - no fallbacks allowed
  }
}

// Load labor costs from S3 file (fast & reliable)
async function loadLaborCostsFromS3() {
  try {
    const config = await loadPricingConfig(false); // Client mode - use S3
    
    if (config && (config.source === 'S3' || config.source === 'RDS') && config.laborCosts) {
      laborCostsCache = config.laborCosts;
      laborCostsCacheTime = Date.now();
      // console.log('💰 Loaded labor costs from S3:', laborCostsCache);
    } else {
      throw new Error('Received invalid pricing data or missing labor costs');
    }
  } catch (error) {
    console.error('❌ Cannot load labor costs:', error.message);
    throw error; // Re-throw the error - no fallbacks allowed
  }
}

// Get current connector pricing (with cache) - S3 only
function getCurrentConnectorPricing() {
  const now = Date.now();
  
  // If cache is expired or doesn't exist, load from S3
  if (!connectorPricingCache || (now - connectorPricingCacheTime) > CONNECTOR_CACHE_DURATION) {
    // Load asynchronously but return current values immediately
    loadConnectorPricingFromS3().catch(error => {
      console.error('❌ Failed to load connector pricing from S3:', error.message);
    });
    return { panelMountPricing: {}, inlinePricing: {} }; // Return empty objects while loading
  }
  
  return connectorPricingCache;
}

// Get current labor costs (with cache) - S3 only
function getCurrentLaborCosts() {
  const now = Date.now();
  
  // If cache is expired or doesn't exist, load from S3
  if (!laborCostsCache || (now - laborCostsCacheTime) > LABOR_COSTS_CACHE_DURATION) {
    // Load asynchronously but return current values immediately
    loadLaborCostsFromS3().catch(error => {
      console.error('❌ Failed to load labor costs from S3:', error.message);
    });
    return { standard: 2.50 }; // Return default while loading
  }
  
  return laborCostsCache;
}

// Get default labor cost from S3
function getDefaultLaborCost() {
  const laborCosts = getCurrentLaborCosts();
  return laborCosts.standard || 2.50; // Fallback to 2.50 if not available
}

// Refresh connector pricing cache (call this after updates)
export const refreshConnectorPricingCache = () => {
  connectorPricingCache = null;
  connectorPricingCacheTime = 0;
  loadConnectorPricingFromS3();
};

// Refresh labor costs cache (call this after updates)
export const refreshLaborCostsCache = () => {
  laborCostsCache = null;
  laborCostsCacheTime = 0;
  loadLaborCostsFromS3();
};

// Cache for finishing pricing from database
let finishingPricingCache = null;
let finishingPricingCacheTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Custom 2" heat shrink: tube = heat-shrink body color; ribbon = printed text/logo color.
 * Disabled colors stay off until you enable them in pricing-config.json / admin.
 */
export const DEFAULT_CUSTOM_HEAT_SHRINK_OPTIONS = {
  tubeColors: {
    black: true,
    red: true,
    white: true,
    blue: true,
    purple: true,
    yellow: true,
    orange: true,
    green: true
  },
  ribbonColors: {
    white: true,
    black: true,
    metallicSilver: false
  }
};

let customHeatShrinkOptionsCache = null;

// Fallback finishing pricing (used while S3 cache is loading or unavailable).
// Must match typical finishingPricing.heatshrinkWrap / customHeatShrinkPerEnd so clear+custom shows clear + custom.
const finishingPricing = {
  heatShrink: {
    'none': 0,
    'clear': 1,
    'custom': 3
  },
  codingRings: {
    'none': 0,
    'brown': 0,
    'red': 0,
    'orange': 0,
    'yellow': 0,
    'green': 0,
    'blue': 0,
    'violet': 0,
    'grey': 0,
    'white': 0
  }
};

// Load finishing pricing from S3 file (fast & reliable)
async function loadFinishingPricingFromS3() {
  try {
    const config = await loadPricingConfig(false); // Client mode - use S3
    
    if (config && (config.source === 'S3' || config.source === 'RDS') && config.finishingPricing) {
      customHeatShrinkOptionsCache = config.customHeatShrinkOptions || DEFAULT_CUSTOM_HEAT_SHRINK_OPTIONS;
      finishingPricingCache = {
        heatShrink: {
          'none': 0,
          // Avoid `|| 0`: missing heatshrinkWrap was pricing clear at $0 and undercharging clear+custom.
          'clear': config.finishingPricing.heatshrinkWrap ?? 1,
          'custom': config.finishingPricing.customHeatShrinkPerEnd ?? 3
        },
        codingRings: {
          'none': 0,
          'brown': config.finishingPricing.codingRings || 0,
          'red': config.finishingPricing.codingRings || 0,
          'orange': config.finishingPricing.codingRings || 0,
          'yellow': config.finishingPricing.codingRings || 0,
          'green': config.finishingPricing.codingRings || 0,
          'blue': config.finishingPricing.codingRings || 0,
          'violet': config.finishingPricing.codingRings || 0,
          'grey': config.finishingPricing.codingRings || 0,
          'white': config.finishingPricing.codingRings || 0
        }
      };
      finishingPricingCacheTime = Date.now();
      // console.log('💰 Loaded finishing pricing from S3:', config.finishingPricing);
    } else {
      throw new Error('Received invalid pricing data or missing finishing pricing');
    }
  } catch (error) {
    console.error('❌ Cannot load finishing pricing:', error.message);
    throw error; // Re-throw the error - no fallbacks allowed
  }
}

// Get current finishing pricing (with cache) - S3 only
function getCurrentFinishingPricing() {
  const now = Date.now();
  
  // If cache is expired or doesn't exist, load from S3
  if (!finishingPricingCache || (now - finishingPricingCacheTime) > CACHE_DURATION) {
    // Load asynchronously but return current values immediately
    loadFinishingPricingFromS3().catch(error => {
      console.error('❌ Failed to load finishing pricing from S3:', error.message);
    });
    return finishingPricing; // Return hardcoded values while loading
  }
  
  return finishingPricingCache;
}

// Refresh finishing pricing cache (call this after updates)
export const refreshFinishingPricingCache = () => {
  finishingPricingCache = null;
  finishingPricingCacheTime = 0;
  loadFinishingPricingFromS3();
};

// Helper function to round price to 2 decimal places
const roundPrice = (price) => {
	return Math.round(price * 100) / 100;
};

// Helper function to round UP to the next .99 (e.g., 16.74 -> 16.99, 31.11 -> 31.99, 31.99 stays 31.99)
export const roundUpToPoint99 = (price) => {
	const cents = Math.ceil(price * 100);
	const dollars = Math.floor(cents / 100);
	const targetCents = dollars * 100 + 99; // current dollar .99
	if (cents <= targetCents) {
		return targetCents / 100;
	}
	// move to next dollar .99
	return (dollars + 1) * 100 / 100 + 0.99;
};

// Helper function to format price in USD
export const formatPrice = (price) => {
  // Round to 2 decimal places to avoid floating point precision issues
  const roundedPrice = roundPrice(price);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(roundedPrice);
};

// Get connector pricing
export const getConnectorPricing = async (connector, isPanelMount = false) => {
  // Load fresh data from S3 for async operations
  await loadConnectorPricingFromS3();
  const { panelMountPricing, inlinePricing } = getCurrentConnectorPricing();
  const pricing = isPanelMount ? panelMountPricing : inlinePricing;
  return pricing[connector] || { partCost: 0, laborCost: getDefaultLaborCost() };
};

// Get finishing option pricing (synchronous version for UI components)
/** Tube/ribbon availability toggles for custom printed heat shrink */
export function getCustomHeatShrinkOptionsSync() {
  return customHeatShrinkOptionsCache || DEFAULT_CUSTOM_HEAT_SHRINK_OPTIONS;
}

export const getFinishingPricingSync = (option, type) => {
  const currentFinishingPricing = getCurrentFinishingPricing();
  if (option === 'heatShrink') {
    return currentFinishingPricing.heatShrink[type] || 0;
  }
  
  if (option === 'codingRings') {
    return currentFinishingPricing.codingRings[type] || 0;
  }
  
  return 0;
};

/**
 * Per-end heat shrink price: clear+custom charges clear + custom on that end.
 */
export function getHeatShrinkEndPrice(heatShrinkType) {
  if (!heatShrinkType || heatShrinkType === 'none') return 0;
  const clear = getFinishingPricingSync('heatShrink', 'clear');
  const custom = getFinishingPricingSync('heatShrink', 'custom');
  if (heatShrinkType === 'clear') return clear;
  if (heatShrinkType === 'custom') return custom;
  if (heatShrinkType === 'clear+custom') return clear + custom;
  return 0;
}

// Get finishing option pricing (async version for server-side calculations)
export const getFinishingPricing = async (option, type) => {
  // Load fresh data from S3 for async operations
  await loadFinishingPricingFromS3();
  const currentFinishingPricing = getCurrentFinishingPricing();
  return currentFinishingPricing[option]?.[type] || 0;
};

// Generate coding ring SKU
export const getCodingRingSKU = (color) => {
  const colorMap = {
    'black': '0',
    'brown': '1', 
    'red': '2',
    'orange': '3',
    'yellow': '4',
    'green': '5',
    'blue': '6',
    'violet': '7',
    'grey': '8',
    'white': '9'
  };
  
  return colorMap[color] ? `XXR-${colorMap[color]}` : null;
};

// Helper function to identify XLR connectors
function isXLRConnector(conn) {
  if (!conn) return false;
  
  return (
    (conn.connector && (
      conn.connector.includes('XLR') ||
      conn.connector.includes('XL') ||
      conn.connector.includes('NC3') ||
      conn.connector.includes('NC5')
    )) ||
    (conn.skuCode && (
      conn.skuCode.includes('XLR') ||
      conn.skuCode.includes('XL') ||
      conn.skuCode.includes('NC3') ||
      conn.skuCode.includes('NC5')
    )) ||
    (conn.id && (
      conn.id.includes('XLR') ||
      conn.id.includes('XL') ||
      conn.id.includes('NC3') ||
      conn.id.includes('NC5')
    )) ||
    (conn.family === 'XLR')
  );
}

// Helper to map UI finishing option values to config keys
function mapFinishingOption(option, value) {
  if (option === 'heatShrink') {
    if (value === 'none') return 'none';
    if (value === 'clear+custom') return 'clear+custom';
    if (value === 'custom') return 'custom';
    if (typeof value === 'string' && value.toLowerCase().includes('clear')) return 'clear';
  }
  if (option === 'codingRings') {
    // Accept color names or 'none'
    if (typeof value === 'string') {
      const v = value.toLowerCase();
      if ([
        'black','brown','red','orange','yellow','green','blue','violet','grey','white'
      ].includes(v)) return v;
      if (v === 'none') return 'none';
    }
  }
  return value;
}

// Updated getConnectorPricingFromList to prioritize connector IDs over SKU codes
export async function getConnectorPricingFromList(connectorId, variant) {
  console.log('🔍 getConnectorPricingFromList called with:', { connectorId, variant });
  
  // Import auto-generated mappings for fallback
  const { mapConnectorId, getPricingSection } = await import('./connectorMappings.js');
  
  // Determine the effective connector ID and mount type
  let effectiveConnectorId = connectorId;
  let mountType = 'inline'; // default
  
  // Check if we need to map the connector ID
  if (!connectorId.includes('_cable_mount_') && !connectorId.includes('_panel_mount_') && 
      !connectorId.includes('NC3') && !connectorId.includes('NC5') && 
      !connectorId.includes('2TR') && !connectorId.includes('3TR') &&
      !connectorId.includes('IEC_') && !connectorId.includes('NEMA_')) {
    // This looks like a SKU code, try to map it
    effectiveConnectorId = mapConnectorId(connectorId);
    // console.log('DEBUG: Mapped SKU code:', connectorId, 'to connector ID:', effectiveConnectorId);
  }
  
  // Enhanced mount type determination
  if (effectiveConnectorId.includes('_panel_mount_')) {
    mountType = 'panel';
    console.log('🔍 Mount type determined from connector ID (panel):', effectiveConnectorId);
  } else if (effectiveConnectorId.includes('_cable_mount_')) {
    mountType = 'inline';
    console.log('🔍 Mount type determined from connector ID (inline):', effectiveConnectorId);
  } else if (variant === 'panel-mount' || variant === 'PNL' || variant === 'panel') {
    mountType = 'panel';
    console.log('🔍 Mount type determined from variant (panel):', variant);
  } else if (variant === 'standard' || variant === 'STD' || variant === 'inline' || variant === 'cable-mount') {
    mountType = 'inline';
    console.log('🔍 Mount type determined from variant (inline):', variant);
  } else {
    // Default to inline for unknown variants
    mountType = 'inline';
    console.log('🔍 Mount type defaulted to inline for variant:', variant);
  }
  
  // console.log('DEBUG: Effective connector ID:', effectiveConnectorId, 'Mount type:', mountType);
  
  // Load pricing config to get the latest pricing
  const pricingConfig = await loadPricingConfig(false); // Client mode - use S3
  
  // Use auto-generated pricing section determination
  const pricingSection = getPricingSection(effectiveConnectorId, variant);
  const pricing = pricingConfig[pricingSection];
  // console.log('DEBUG: Using', pricingSection, 'for connector:', effectiveConnectorId, 'variant:', variant);
  
  // Try to find pricing using the effective connector ID
  let result = pricing[effectiveConnectorId];
  
  // If not found, try searching in all connector arrays as fallback
  if (!result) {
    const allConnectors = [
      ...connectorList,
      ...audioConnectors,
      ...powerConnectors,
      ...dmxConnectors
    ];
    
    const connector = allConnectors.find(c => 
      c.partNumber === effectiveConnectorId || 
      c.id === effectiveConnectorId ||
      c.skuCode === connectorId // fallback to original input
    );
    
    if (connector) {
      result = mountType === 'panel' ? connector.panelMountPricing : connector.inlinePricing;
      // console.log('DEBUG: Found pricing in connector object:', result);
    }
  }
  
  // If still no result, try fallback search by connector ID regardless of mount type
  if (!result) {
    console.log(`🔍 Fallback search: No pricing found for ${effectiveConnectorId} in ${pricingSection}, searching all pricing sections...`);
    
    // Try searching in both panel mount and inline pricing sections
    const allPricing = {
      ...pricingConfig.panelMountPricing,
      ...pricingConfig.inlinePricing
    };
    
    result = allPricing[effectiveConnectorId];
    
    if (result) {
      console.log(`✅ Found pricing in fallback search for ${effectiveConnectorId}:`, result);
    } else {
      // Still no result, provide default pricing with warning
      result = { partCost: 0, laborCost: getDefaultLaborCost() };
      console.warn(`WARNING: No pricing found for connector: ${connectorId} (effective: ${effectiveConnectorId}), mount type: ${mountType}`);
      console.warn(`Available pricing keys in ${pricingSection}:`, Object.keys(pricing).slice(0, 10)); // Show first 10 for debugging
      console.warn(`Total available pricing keys across all sections:`, Object.keys(allPricing).length);
    }
  }
  
  // Round the pricing results to 2 decimal places
  result = {
    partCost: roundPrice(result.partCost || 0),
    laborCost: roundPrice(result.laborCost || 0)
  };
  
  return result;
}

// New utility function to get pricing directly from RDS database structure
export async function getPricingFromRDSStructure(connectorId, mountType = 'inline') {
  // console.log('DEBUG: getPricingFromRDSStructure called with:', connectorId, mountType);
  
  // Load pricing config
  const pricingConfig = await loadPricingConfig(false); // Client mode - use S3
  
  // Determine which pricing section to use based on mount type
  const pricingSection = mountType === 'panel' ? 'panelMountPricing' : 'inlinePricing';
  const pricing = pricingConfig[pricingSection];
  
  // Try to find the connector in the pricing data
  let result = pricing[connectorId];
  
  if (result) {
    // console.log('DEBUG: Found pricing in RDS structure:', result);
    return {
      partCost: roundPrice(result.partCost || 0),
      laborCost: roundPrice(result.laborCost || 0)
    };
  }
  
  // Fallback: search in both pricing sections regardless of mount type
  console.log(`🔍 RDS Fallback search: No pricing found for ${connectorId} in ${pricingSection}, searching all pricing sections...`);
  
  const allPricing = {
    ...pricingConfig.panelMountPricing,
    ...pricingConfig.inlinePricing
  };
  
  result = allPricing[connectorId];
  
  if (result) {
    console.log(`✅ Found pricing in RDS fallback search for ${connectorId}:`, result);
    return {
      partCost: roundPrice(result.partCost || 0),
      laborCost: roundPrice(result.laborCost || 0)
    };
  }
  
  // If not found, return null to allow fallback
  // console.log('DEBUG: No pricing found in RDS structure for:', connectorId, 'in section:', pricingSection);
  return null;
}

// Enhanced connector pricing lookup that prioritizes RDS database structure
export async function getConnectorPricingOptimized(connectorId, variant = 'standard') {
  console.log('🔍 getConnectorPricingOptimized called with:', { connectorId, variant });
  
  // Import mappings
  const { mapConnectorId, getPricingSection } = await import('./connectorMappings.js');
  
  // Determine effective connector ID
  let effectiveConnectorId = connectorId;
  let mountType = 'inline';
  
  // Check if we need to map the connector ID
  if (!connectorId.includes('_cable_mount_') && !connectorId.includes('_panel_mount_') && 
      !connectorId.includes('NC3') && !connectorId.includes('NC5') && 
      !connectorId.includes('2TR') && !connectorId.includes('3TR') &&
      !connectorId.includes('IEC_') && !connectorId.includes('NEMA_')) {
    effectiveConnectorId = mapConnectorId(connectorId);
    console.log('🔍 Mapped connector ID:', connectorId, 'to:', effectiveConnectorId);
  }
  
  // Enhanced mount type determination
  if (effectiveConnectorId.includes('_panel_mount_')) {
    mountType = 'panel';
    console.log('🔍 Mount type determined from connector ID (panel):', effectiveConnectorId);
  } else if (effectiveConnectorId.includes('_cable_mount_')) {
    mountType = 'inline';
    console.log('🔍 Mount type determined from connector ID (inline):', effectiveConnectorId);
  } else if (variant === 'panel-mount' || variant === 'PNL' || variant === 'panel') {
    mountType = 'panel';
    console.log('🔍 Mount type determined from variant (panel):', variant);
  } else if (variant === 'standard' || variant === 'STD' || variant === 'inline' || variant === 'cable-mount') {
    mountType = 'inline';
    console.log('🔍 Mount type determined from variant (inline):', variant);
  } else {
    // Default to inline for unknown variants
    mountType = 'inline';
    console.log('🔍 Mount type defaulted to inline for variant:', variant);
  }
  
  console.log('🔍 Final determination - Effective ID:', effectiveConnectorId, 'Mount type:', mountType);
  
  // Try RDS structure first
  let result = await getPricingFromRDSStructure(effectiveConnectorId, mountType);
  
  // If not found in RDS structure, fall back to legacy pricing lookup
  if (!result) {
    console.log('🔍 Falling back to legacy pricing lookup');
    result = await getConnectorPricingFromList(connectorId, variant);
  }
  
  return result;
}

// Utility to count all XLR connectors in a config
export function countXLRConnectors(config) {
  let count = 0;
  const isXLR = (conn) =>
    conn && (
      (conn.connector && (
        conn.connector.includes('XLR') ||
        conn.connector.includes('XL') ||
        conn.connector.includes('NC3') ||
        conn.connector.includes('NC5')
      )) ||
      (conn.skuCode && (
        conn.skuCode.includes('XLR') ||
        conn.skuCode.includes('XL') ||
        conn.skuCode.includes('NC3') ||
        conn.skuCode.includes('NC5')
      )) ||
      (conn.id && (
        conn.id.includes('XLR') ||
        conn.id.includes('XL') ||
        conn.id.includes('NC3') ||
        conn.id.includes('NC5')
      )) ||
      (conn.family === 'XLR')
    );
  if (isXLR(config.connectors?.endA)) count++;
  if (isXLR(config.connectors?.endB)) count++;
  if (config.hybridConnectors?.connectors) {
    for (const conn of Object.values(config.hybridConnectors.connectors)) {
      if (isXLR(conn)) count++;
    }
  }
  if (config.wyeConnectors?.connectors) {
    for (const conn of Object.values(config.wyeConnectors.connectors)) {
      if (isXLR(conn)) count++;
    }
  }
  if (config.zipConnectors?.connectors) {
    for (const conn of Object.values(config.zipConnectors.connectors)) {
      if (isXLR(conn)) count++;
    }
  }
  return count;
}

// Calculate total cable price
export const calculateCablePrice = async (config) => {
  
  // Only check endA/endB for non-hybrid cable types
  const isHybridCable = config.style && ['hybrid', 'zip', 'zip-audio-audio', 'wye', 'hybrid-power-audio-audio'].includes(config.style);
  
  if (!isHybridCable) {
    // Defensive logging for endA and endB (only for standard cables)
    if (!config.endA || !config.endA.connector) {
      console.warn('WARNING: endA is missing or incomplete:', config.endA);
    } else {
      // Debug logging removed for production
    }
    if (!config.endB || !config.endB.connector) {
      console.warn('WARNING: endB is missing or incomplete:', config.endB);
    } else {
      // Debug logging removed for production
    }
  }
  
  const {
    type,
    length,
    endA,
    endB,
    finishes,
    laborCost,
    hybridConnectors, // Add this for hybrid cables
    wireGauge, // Add wire gauge for power cables
    style, // Add style for Wye cables
    wireType // Add wire type to detect DMX cables
  } = config;

  // Load admin-modifiable pricing config
  const pricingConfig = await loadPricingConfig(false); // Client mode - use S3
  // console.log('DEBUG: Loaded pricing config:', pricingConfig);
  
  const cableBasePrices = pricingConfig.cableBasePrices || {};
  const powerWireGaugePricing = pricingConfig.powerWireGaugePricing || {};
  const wyeCablePricing = pricingConfig.wyeCablePricing || {};

  // Detect DMX cables: ONLY check if wire type is PSDMX
  // Hybrid is hybrid, wye is wye, dmx is dmx - only wire type matters for pricing
  let effectiveType = type;
  
  if (wireType === 'PSDMX') {
    effectiveType = 'dmx';
  }

  // Base cable cost - handle wire gauge pricing for power cables and Wye cable pricing
  let basePrice = typeof cableBasePrices[effectiveType] === 'number' ? cableBasePrices[effectiveType] : 0;
  
  // For power cables, use wire gauge pricing if available
  if (type === 'power' && wireGauge) {
    // UI stores gauge as "14"; pricing config keys are "14AWG" — accept either
    const gaugeValue = typeof wireGauge === 'string' ? wireGauge : wireGauge.gauge;
    if (gaugeValue) {
      const withAwg = /AWG$/i.test(gaugeValue) ? gaugeValue : `${gaugeValue}AWG`;
      const gaugePrice = powerWireGaugePricing[gaugeValue] ?? powerWireGaugePricing[withAwg];
      if (typeof gaugePrice === 'number') {
        basePrice = gaugePrice;
      }
    }
  }
  
  // For Wye cables, use specific Wye pricing if available
  if (style === 'wye' && wyeCablePricing[type]) {
    basePrice = wyeCablePricing[type];
  }
  
  // Convert base price from per-foot to per-inch since length is now in inches
  const basePricePerInch = basePrice / 12;
  const rawCableCost = basePricePerInch * parseFloat(length);
  const cableCost = roundPrice(rawCableCost);

  // Handle hybrid and Wye connectors if present
  let connectorCost = 0;
  let totalLaborCost = 0; // Initialize labor cost accumulator
  
  if (hybridConnectors && hybridConnectors.connectors) {
    for (const [pointId, connector] of Object.entries(hybridConnectors.connectors)) {
      if (connector && connector.id) {
        // Use connector.id as primary identifier for pricing
        const connectorPricing = await getConnectorPricingOptimized(connector.id, connector.configuration);
        connectorCost += connectorPricing.partCost;
        totalLaborCost += connectorPricing.laborCost; // Add labor cost for this connector
      } else if (connector && connector.skuCode) {
        // Fallback to skuCode if id is not available
        const connectorPricing = await getConnectorPricingOptimized(connector.skuCode, connector.configuration);
        connectorCost += connectorPricing.partCost;
        totalLaborCost += connectorPricing.laborCost; // Add labor cost for this connector
      } else {
        console.warn(`WARNING: Hybrid connector at point ${pointId} has no id or skuCode:`, connector);
      }
    }
    connectorCost = roundPrice(connectorCost);
  } else if (config.wyeConnectors && config.wyeConnectors.connectors) {
    for (const [pointId, connector] of Object.entries(config.wyeConnectors.connectors)) {
      if (connector && connector.id) {
        // Use connector.id as primary identifier for pricing
        // console.log('DEBUG: Pricing Wye connector', pointId, 'using id:', connector.id, connector.configuration);
        const connectorPricing = await getConnectorPricingOptimized(connector.id, connector.configuration);
        // console.log('DEBUG: Connector', pointId, connector.id, 'pricing:', connectorPricing);
        connectorCost += connectorPricing.partCost;
        totalLaborCost += connectorPricing.laborCost; // Add labor cost for this connector
      } else if (connector && connector.skuCode) {
        // Fallback to skuCode if id is not available
        // console.log('DEBUG: Pricing Wye connector', pointId, 'fallback to skuCode:', connector.skuCode, connector.configuration);
        const connectorPricing = await getConnectorPricingOptimized(connector.skuCode, connector.configuration);
        // console.log('DEBUG: Connector', pointId, connector.skuCode, 'pricing:', connectorPricing);
        connectorCost += connectorPricing.partCost;
        totalLaborCost += connectorPricing.laborCost; // Add labor cost for this connector
      } else {
        console.warn(`WARNING: Wye connector at point ${pointId} has no id or skuCode:`, connector);
      }
    }
    connectorCost = roundPrice(connectorCost);
  } else if (endA?.connector || endB?.connector) {
    // Standard connector costs - use new connectorList pricing
    // console.log('DEBUG: Pricing endA:', endA?.connector, endA?.variant);
    const endAPricing = endA?.connector ? await getConnectorPricingOptimized(endA.connector, endA.variant) : { partCost: 0, laborCost: getDefaultLaborCost() };
    // console.log('DEBUG: Pricing endB:', endB?.connector, endB?.variant);
    const endBPricing = endB?.connector ? await getConnectorPricingOptimized(endB.connector, endB.variant) : { partCost: 0, laborCost: getDefaultLaborCost() };
    connectorCost = roundPrice(endAPricing.partCost + endBPricing.partCost);
    totalLaborCost = endAPricing.laborCost + endBPricing.laborCost; // Sum labor costs from both connectors
  } else {
    // console.log('DEBUG: No connectors found for pricing');
    connectorCost = 0;
    totalLaborCost = 0;
  }

  // console.log('DEBUG: Connector cost:', connectorCost);
  // console.log('DEBUG: Total labor cost from connectors:', totalLaborCost);

  // Finishing costs
  let finishingCost = 0;
  if (finishes) {
    // console.log('DEBUG: Finishes object:', finishes);
    
    // Handle heat shrink - support both old format (single type) and new format (per-connector)
    if (finishes.heatShrink) {
      if (typeof finishes.heatShrink === 'string') {
        // Old format - single type for all connectors
        if (finishes.heatShrink !== 'none') {
          const mapped = mapFinishingOption('heatShrink', finishes.heatShrink);
          finishingCost += getHeatShrinkEndPrice(mapped);
        }
      } else if (typeof finishes.heatShrink === 'object' && finishes.heatShrink.type) {
        // Old format with type property
        if (finishes.heatShrink.type !== 'none') {
          const mapped = mapFinishingOption('heatShrink', finishes.heatShrink.type);
          finishingCost += getHeatShrinkEndPrice(mapped);
        }
      } else {
        // New format - per-connector heat shrink
        // Count all connectors and calculate total cost
        const allConnectors = [];
        
        // Check standard connectors
        if (endA?.connector) {
          allConnectors.push('endA');
        }
        if (endB?.connector) {
          allConnectors.push('endB');
        }
        
        // Check hybrid connectors
        if (hybridConnectors?.connectors) {
          Object.keys(hybridConnectors.connectors).forEach(key => {
            const connector = hybridConnectors.connectors[key];
            if (connector) {
              allConnectors.push(key);
            }
          });
        }
        
        // Check wye connectors
        if (config.wyeConnectors?.connectors) {
          Object.keys(config.wyeConnectors.connectors).forEach(key => {
            const connector = config.wyeConnectors.connectors[key];
            if (connector) {
              allConnectors.push(key);
            }
          });
        }

        // Check zip connectors
        if (config.zipConnectors?.connectors) {
          Object.keys(config.zipConnectors.connectors).forEach(key => {
            const connector = config.zipConnectors.connectors[key];
            if (connector) {
              allConnectors.push(key);
            }
          });
        }
        
        // Calculate cost for each connector
        allConnectors.forEach(connectorKey => {
          const heatShrinkType = finishes.heatShrink[connectorKey] || finishes.heatShrink.global || 'none';
          if (heatShrinkType !== 'none') {
            const mapped = mapFinishingOption('heatShrink', heatShrinkType);
            finishingCost += getHeatShrinkEndPrice(mapped);
          }
        });
      }
    }
    
    // Handle coding rings - support both old format (single color) and new format (per-connector)
    if (finishes.codingRings) {
      if (typeof finishes.codingRings === 'string') {
        // Old format - single color for all connectors
        if (finishes.codingRings !== 'none') {
          const mapped = mapFinishingOption('codingRings', finishes.codingRings);
          const codingRingsPrice = getFinishingPricingSync('codingRings', mapped);
          finishingCost += codingRingsPrice;
        }
      } else {
        // New format - per-connector colors
        // Count XLR connectors and calculate total cost
        const xlrConnectors = [];
        
        // Check standard connectors
        if (endA?.connector && isXLRConnector(endA)) {
          xlrConnectors.push('endA');
        }
        if (endB?.connector && isXLRConnector(endB)) {
          xlrConnectors.push('endB');
        }
        
        // Check hybrid connectors
        if (hybridConnectors?.connectors) {
          Object.keys(hybridConnectors.connectors).forEach(key => {
            const connector = hybridConnectors.connectors[key];
            if (connector && isXLRConnector(connector)) {
              xlrConnectors.push(key);
            }
          });
        }
        
        // Check wye connectors
        if (config.wyeConnectors?.connectors) {
          Object.keys(config.wyeConnectors.connectors).forEach(key => {
            const connector = config.wyeConnectors.connectors[key];
            if (connector && isXLRConnector(connector)) {
              xlrConnectors.push(key);
            }
          });
        }
        
        // Calculate cost for each XLR connector
        xlrConnectors.forEach(connectorKey => {
          const color = finishes.codingRings[connectorKey] || finishes.codingRings.global || 'none';
          if (color !== 'none') {
            const mapped = mapFinishingOption('codingRings', color);
            const codingRingsPrice = getFinishingPricingSync('codingRings', mapped);
            finishingCost += codingRingsPrice;
          }
        });
      }
    }
  }
  finishingCost = roundPrice(finishingCost);
  // console.log('DEBUG: Finishing cost:', finishingCost);

  // Labor cost - use the calculated total from connectors instead of config.laborCost
  totalLaborCost = roundPrice(totalLaborCost);
  // console.log('DEBUG: Final labor cost:', totalLaborCost);

  // Calculate total, then round UP to the next .99 for final displayed/sold price
  const totalCostRaw = roundPrice(cableCost + connectorCost + finishingCost + totalLaborCost);
  const totalCost = roundUpToPoint99(totalCostRaw);
  // console.log('DEBUG: Total cost (raw):', totalCostRaw, 'Total cost (rounded to .99):', totalCost);

  return {
    cableCost,
    connectorCost,
    finishingCost,
    laborCost: totalLaborCost,
    totalCost,
    totalCostRaw
  };
};

// Calculate PO cost
export const calculatePOCost = async (config) => {
  const cablePrice = await calculateCablePrice(config);
  
  // Apply any PO-specific pricing rules here
  // For now, just return the cable price
  return cablePrice;
};

// Cleanup function for when component unmounts
export const cleanupPricingUpdates = () => {
  if (pricingEventSource) {
    pricingEventSource.close();
    pricingEventSource = null;
  }
  pricingUpdateCallbacks = [];
}; 